"use client";

import {
  ArrowLeft,
  ArrowRight,
  NavArrowLeft,
  NavArrowRight,
} from "iconoir-react";

import { buildPageList } from "@/lib/pagination";
import { cn } from "@/lib/utils";

interface ControlledPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  ariaLabel: string;
  previousLabel: string;
  nextLabel: string;
  getPageLabel?: (page: number) => string;
  variant?: "article" | "customer";
  className?: string;
}

export function ControlledPagination({
  currentPage,
  totalPages,
  onPageChange,
  ariaLabel,
  previousLabel,
  nextLabel,
  getPageLabel,
  variant = "article",
  className,
}: ControlledPaginationProps) {
  const customerVariant = variant === "customer";
  const showPrevious = !customerVariant || currentPage > 1;
  const showNext = !customerVariant || currentPage < totalPages;

  if (totalPages <= 1) return null;

  return (
    <nav
      className={cn("flex items-center gap-2", className)}
      aria-label={ariaLabel}
    >
      {showPrevious ? (
        <PageButton
          variant={variant}
          disabled={!customerVariant && currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label={previousLabel}
        >
          {customerVariant ? (
            <ArrowLeft className="size-4" />
          ) : (
            <NavArrowLeft className="size-3" aria-hidden />
          )}
        </PageButton>
      ) : null}

      {buildPageList(currentPage, totalPages).map((item) =>
        typeof item === "string" ? (
          <PaginationEllipsis key={item} variant={variant} />
        ) : (
          <PageButton
            key={item}
            variant={variant}
            active={item === currentPage}
            onClick={() => onPageChange(item)}
            aria-current={item === currentPage ? "page" : undefined}
            aria-label={getPageLabel?.(item)}
          >
            {item}
          </PageButton>
        )
      )}

      {showNext ? (
        <PageButton
          variant={variant}
          disabled={!customerVariant && currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label={nextLabel}
        >
          {customerVariant ? (
            <ArrowRight className="size-4" />
          ) : (
            <NavArrowRight className="size-3" aria-hidden />
          )}
        </PageButton>
      ) : null}
    </nav>
  );
}

function PaginationEllipsis({
  variant,
}: {
  variant: NonNullable<ControlledPaginationProps["variant"]>;
}) {
  if (variant === "customer") {
    return (
      <span
        className="text-grey-950 text-ds-body-xs inline-flex size-8 items-center justify-center font-semibold"
        aria-hidden="true"
      >
        …
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className="bg-grey-50 border-grey-100 text-grey-950 flex size-8 items-center justify-center rounded-lg border text-[12px] tracking-[-0.24px]"
    >
      ...
    </span>
  );
}

function PageButton({
  active,
  variant,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  variant: NonNullable<ControlledPaginationProps["variant"]>;
}) {
  return (
    <button
      type="button"
      className={cn(
        variant === "customer"
          ? [
              "border-grey-100 bg-grey-50 text-grey-950 rounded-ds-m text-ds-body-xs inline-flex size-8 items-center justify-center border transition-colors",
              "hover:bg-white",
              "focus-visible:border-pulse-700 focus-visible:outline-none",
              active && "text-grey-400",
            ]
          : [
              "flex size-8 items-center justify-center rounded-lg border text-[12px] tracking-[-0.24px] transition-colors",
              active
                ? "bg-grey-200 border-grey-300 text-grey-950"
                : "bg-grey-50 border-grey-100 text-grey-950 hover:bg-grey-100",
              "disabled:pointer-events-none disabled:opacity-40",
            ],
        className
      )}
      {...props}
    />
  );
}
